import React from 'react';
import { Users } from 'lucide-react';
import { WeeklyRosterData } from '../../types';
import PlayerWeeklyCard from './PlayerWeeklyCard';

interface WeeklyRosterGridProps {
  rosterData: WeeklyRosterData;
  onPlayerClick?: (playerId: number) => void;
  sortBy?: 'name' | 'points' | 'position' | 'status';
  onSortChange?: (sortBy: 'name' | 'points' | 'position' | 'status') => void;
}

/**
 * WeeklyRosterGrid - Grid layout for weekly player cards
 * Features:
 * - Responsive grid layout
 * - Sorting by various criteria
 * - Separate sections for starters vs bench
 * - Visual grouping by lineup position
 */
const WeeklyRosterGrid: React.FC<WeeklyRosterGridProps> = ({
  rosterData,
  onPlayerClick,
  sortBy = 'status',
  onSortChange
}) => {
  const { roster, week } = rosterData;

  // Separate starters and bench players
  const starters = roster.filter(player => player.is_starter);
  const bench = roster.filter(player => !player.is_starter && player.lineup_slot !== 'IR');
  const irPlayers = roster.filter(player => player.lineup_slot === 'IR');

  // Sort function
  const sortPlayers = (players: typeof roster, criteria: typeof sortBy) => {
    return [...players].sort((a, b) => {
      switch (criteria) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'points':
          return b.week_points - a.week_points;
        case 'position':
          const positionOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'D/ST'];
          const aIndex = positionOrder.indexOf(a.position);
          const bIndex = positionOrder.indexOf(b.position);
          return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
        case 'status':
        default:
          // Sort by starter status, then by lineup slot order
          const slotOrder = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'D/ST', 'BENCH'];
          const aSlotIndex = slotOrder.indexOf(a.lineup_slot);
          const bSlotIndex = slotOrder.indexOf(b.lineup_slot);
          return (aSlotIndex === -1 ? 999 : aSlotIndex) - (bSlotIndex === -1 ? 999 : bSlotIndex);
      }
    });
  };

  const sortedStarters = sortPlayers(starters, sortBy);
  const sortedBench = sortPlayers(bench, sortBy);
  const sortedIR = sortPlayers(irPlayers, sortBy);

  const PlayerSection: React.FC<{
    title: string;
    players: typeof roster;
    icon: React.ReactNode;
    bgColor?: string;
  }> = ({ title, players, icon, bgColor = 'bg-background-primary/50' }) => {
    if (players.length === 0) return null;

    return (
      <div className="mb-8">
        <div className={`${bgColor} rounded-lg p-4`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              {icon}
              <span>{title}</span>
              <span className="text-text-secondary text-sm">({players.length})</span>
            </h3>
            
            {/* Sort Controls */}
            {onSortChange && (
              <div className="flex items-center space-x-2">
                <span className="text-text-secondary text-sm">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => onSortChange(e.target.value as typeof sortBy)}
                  className="bg-background-secondary text-text-primary text-sm rounded px-2 py-1 border border-fieldline focus:border-accent-primary focus:outline-none"
                >
                  <option value="status">Lineup Position</option>
                  <option value="points">Week Points</option>
                  <option value="position">Position</option>
                  <option value="name">Name</option>
                </select>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {players.map((player) => (
              <div key={player.player_id} className="relative">
                <PlayerWeeklyCard
                  player={player}
                  selectedWeek={week}
                  onPlayerClick={onPlayerClick}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-text-primary">
          Week {week} Roster
        </h2>
        <div className="text-text-secondary text-sm">
          {roster.length} total players
        </div>
      </div>

      {/* Starters Section */}
      <PlayerSection
        title="Starting Lineup"
        players={sortedStarters}
        icon={<Users className="w-5 h-5 text-accent-primary" />}
        bgColor="bg-accent-primary/5"
      />

      {/* Bench Section */}
      <PlayerSection
        title="Bench"
        players={sortedBench}
        icon={<Users className="w-5 h-5 text-text-secondary" />}
        bgColor="bg-background-primary/30"
      />

      {/* IR Section */}
      {sortedIR.length > 0 && (
        <PlayerSection
          title="Injured Reserve"
          players={sortedIR}
          icon={<Users className="w-5 h-5 text-accent-danger" />}
          bgColor="bg-accent-danger/5"
        />
      )}

      {/* Empty State */}
      {roster.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-text-secondary mb-2">
            No Roster Data
          </h3>
          <p className="text-text-secondary">
            No players found for Week {week}. This might be a future week or data may not be available.
          </p>
        </div>
      )}
    </div>
  );
};

export default WeeklyRosterGrid;
